import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Search,
  ChevronRight
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AdminAppointmentsList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments');
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAppointments = appointments
    .filter(apt => {
      const search = searchTerm.toLowerCase();
      return (
        apt.customer_name?.toLowerCase().includes(search) ||
        apt.city?.toLowerCase().includes(search) ||
        apt.street?.toLowerCase().includes(search) ||
        apt.postal_code?.includes(search)
      );
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800">Bestätigt</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Ausstehend</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Storniert</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 md:py-8" data-testid="appointments-list-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm" className="px-2 md:px-4">
                <ArrowLeft className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Zurück</span>
              </Button>
            </Link>
            <h1 className="font-barlow font-bold text-xl md:text-2xl text-[#1A1A1A]">
              Alle Termine ({filteredAppointments.length})
            </h1>
          </div>
          <Link to="/admin/termine">
            <Button className="btn-primary w-full sm:w-auto">
              <Calendar className="w-4 h-4 mr-2" />
              Kalender
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Suchen nach Name, Stadt, Straße..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Appointments List */}
        <div className="space-y-4">
          {filteredAppointments.length > 0 ? (
            filteredAppointments.map((apt) => (
              <Link to={`/admin/termine?date=${apt.date}`} key={apt.id}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`appointment-${apt.id}`}>
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      {/* Left: Date & Time */}
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-[#9B1B30] text-white rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                          <span className="font-barlow font-bold text-xl md:text-2xl">
                            {format(parseISO(apt.date), 'd')}
                          </span>
                          <span className="font-manrope text-xs uppercase">
                            {format(parseISO(apt.date), 'MMM', { locale: de })}
                          </span>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-barlow font-bold text-lg md:text-xl text-[#1A1A1A]">
                            {format(parseISO(apt.date), 'EEEE', { locale: de })} um {apt.time} Uhr
                          </p>
                          <p className="font-manrope text-gray-700 text-base md:text-lg mt-1">
                            {apt.customer_name}
                          </p>
                          
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{apt.street} {apt.house_number}, {apt.postal_code} {apt.city}</span>
                            </div>
                            {apt.phone && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Phone className="w-4 h-4 flex-shrink-0" />
                                <span>{apt.phone}</span>
                              </div>
                            )}
                            {apt.email && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Mail className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{apt.email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Right: Status & Arrow */}
                      <div className="flex items-center gap-2 md:flex-col md:items-end">
                        {getStatusBadge(apt.status)}
                        <ChevronRight className="w-5 h-5 text-gray-400 hidden md:block" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-manrope text-gray-500">
                  {searchTerm ? 'Keine Termine gefunden' : 'Keine Termine vorhanden'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
